/**
 * Application installable (PWA).
 *
 * `registerServiceWorker()` pose le worker de `public/sw.js` sur un site
 * construit ; en développement, il est au contraire retiré, car il servirait
 * une version figée par-dessus le rechargement à chaud de Vite.
 *
 * `installPrompt` retient l'événement `beforeinstallprompt` de Chrome et Edge
 * pour que l'application puisse proposer l'installation au bon moment (un
 * bouton dans la barre du haut) plutôt que de compter sur l'icône discrète de
 * la barre d'adresse. Safari n'émet pas cet événement : sur iPad et sur Mac,
 * l'installation passe par « Partager → Ajouter à l'écran d'accueil / au
 * Dock », ce que le bouton explique.
 */

/** Événement propre à Chromium ; pas encore dans les types du DOM. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type Listener = (available: boolean) => void;

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<Listener>();

function announce(): void {
  for (const listener of listeners) listener(deferred !== null);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Sans ceci, Chrome affiche sa propre invite : on préfère la nôtre.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    announce();
  });
}

export const installPrompt = {
  /** Vrai quand le navigateur accepte de lancer l'installation sur demande. */
  available: () => deferred !== null,
  /** S'abonner aux changements ; renvoie la fonction de désabonnement. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    listener(deferred !== null);
    return () => listeners.delete(listener);
  },
  /** Lance l'invite du navigateur ; renvoie vrai si l'installation est acceptée. */
  async show(): Promise<boolean> {
    if (!deferred) return false;
    const event = deferred;
    deferred = null;
    announce();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === "accepted";
  },
};

/** L'application tourne-t-elle déjà dans sa propre fenêtre ? */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS avant la prise en charge de display-mode.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (!import.meta.env.PROD) {
    // Un worker resté d'une visite en production sur le même hôte (ou d'un
    // aperçu local) masquerait les modifications en cours : on le retire.
    void navigator.serviceWorker
      .getRegistrations()
      .then((all) => all.forEach((r) => void r.unregister()))
      .catch(() => undefined);
    return;
  }
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // Pas de worker : l'application fonctionne, sans installation ni reprise
      // hors ligne. Rien à signaler à l'utilisateur.
    });
  });
}
